import { useState, useEffect, useRef } from "react";
// import { getAuth } from "firebase/auth"; // Eliminado porque no se usa
import { getFirestore, doc, getDoc, collection, query, where, onSnapshot, updateDoc, getDocs, addDoc, setDoc, arrayUnion, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FaEye, FaBell, FaExchangeAlt, FaMapMarkerAlt, FaCheck, FaTimes, FaHardHat, FaRegHospital, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

import { notificationService } from '../services/notificationService';
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from 'react-i18next';
import { HiOutlineDocumentText } from 'react-icons/hi';
import ReactDOM from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';

const WorkerDashboard = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState<any | null>(null);
  const [certifications, setCertifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState<any[]>([]);
  const bellRef = useRef<HTMLButtonElement>(null);
  const db = getFirestore();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [toast, setToast] = useState<{message: string, type: 'success'|'error'}|null>(null);
  const [locationSaved, setLocationSaved] = useState(false);
  const [locationCapturing, setLocationCapturing] = useState(false);
  const [activeProjects, setActiveProjects] = useState<any[]>([]);
  const [assignedTasks, setAssignedTasks] = useState<any[]>([]);
  const [assignedBusinesses, setAssignedBusinesses] = useState<any[]>([]);
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'unread' | 'read'>('unread');

  // Función para marcar notificación como leída
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markNotificationAsRead(currentUser!.uid, notificationId);
      // Actualizar el estado local
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Componente para notificaciones no leídas
  const UnreadNotifications = ({ notifications, onMarkAsRead }: { notifications: any[], onMarkAsRead: (id: string) => void }) => (
    <div className="space-y-3">
      {notifications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FaBell className="text-3xl mx-auto mb-2 text-gray-300" />
          <p>{t('workerDashboard.noUnreadNotifications')}</p>
        </div>
      ) : (
        notifications.map((notification) => (
          <div
            key={notification.id}
            className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onMarkAsRead(notification.id)}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {notification.type.includes('approved') ? (
                  <FaCheck className="text-green-500" />
                ) : notification.type.includes('rejected') ? (
                  <FaExclamationTriangle className="text-red-500" />
                ) : (
                  <FaInfoCircle className="text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 mb-1">
                  {notification.title}
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  {notification.message}
                </p>
                {notification.reviewNotes && (
                  <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    <strong>{t('workerDashboard.notes')}:</strong> {notification.reviewNotes}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(notification.createdAt).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
                </p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Componente para notificaciones leídas
  const ReadNotifications = ({ notifications }: { notifications: any[] }) => (
    <div className="space-y-3">
      {notifications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FaCheck className="text-3xl mx-auto mb-2 text-gray-300" />
          <p>{t('workerDashboard.noReadNotifications')}</p>
        </div>
      ) : (
        notifications.map((notification) => (
          <div
            key={notification.id}
            className="p-4 rounded-lg border border-gray-200 bg-gray-50 opacity-75"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {notification.type.includes('approved') ? (
                  <FaCheck className="text-green-500" />
                ) : notification.type.includes('rejected') ? (
                  <FaExclamationTriangle className="text-red-500" />
                ) : (
                  <FaInfoCircle className="text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-700 mb-1">
                  {notification.title}
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  {notification.message}
                </p>
                {notification.reviewNotes && (
                  <p className="text-xs text-gray-500 bg-white p-2 rounded">
                    <strong>{t('workerDashboard.notes')}:</strong> {notification.reviewNotes}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(notification.createdAt).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
                </p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Pasos de onboarding esperados
  const ONBOARDING_STEPS = [
    { id: 'id', label: t('workerDashboard.onboardingStepIdentity') },
    { id: 'address', label: t('workerDashboard.onboardingStepAddress') },
    { id: 'experience', label: t('workerDashboard.onboardingStepExperience') },
    { id: 'photo', label: t('workerDashboard.onboardingStepPhoto') },
    { id: 'certifications', label: t('workerDashboard.onboardingStepCertifications') },
  ];

  useEffect(() => {
    // Eliminar redirección, permitir ambos roles
    // if (userData && userData.role !== "Worker") {
    //   navigate("/dashboard");
    // }
  }, [userData, navigate]);

  // Formatear la fecha según el idioma
  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.getMonth();
    const year = date.getFullYear();
    
    const monthsES = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    const monthsEN = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    return i18n.language === 'es' 
      ? `${day}-${monthsES[month]}-${year}`
      : `${monthsEN[month]}-${day}-${year}`;
  };

  // Formatear la hora
  const formatTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Función para obtener los datos del usuario
  const fetchUserData = async () => {
    try {
      if (!currentUser) return;
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (userDoc.exists()) {
        const user = userDoc.data() as any;
        setUserData(user);
      }
    } catch (error) {
      console.error("Error al obtener datos del usuario:", error);
      setError("Error al cargar los datos del usuario");
    }
  };

  // Función para obtener las certificaciones
  const fetchCertifications = async () => {
    try {
      if (!currentUser) return;
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.certifications && Array.isArray(userData.certifications)) {
          // Procesar certificaciones para usar los campos correctos según el idioma
          const processedCertifications = userData.certifications.map((cert: any) => {
            const isEnglish = i18n.language === 'en';
            return {
              ...cert,
              name: isEnglish ? cert.name_en : cert.name_es,
              issuer: isEnglish ? cert.issuer_en : cert.issuer_es,
              description: isEnglish ? cert.description_en : cert.description_es,
              duration: isEnglish ? cert.duration_en : cert.duration_es,
              status: cert['cert-status'] || 'pending'
            };
          });
          setCertifications(processedCertifications);
        } else {
          setCertifications([]);
        }
      }
    } catch (error) {
      console.error("Error al obtener certificaciones:", error);
      setError("Error al cargar las certificaciones");
    }
  };

  useEffect(() => {
    // Solo cargar datos si el usuario está autenticado
    if (currentUser) {
      Promise.all([
        fetchUserData(),
        fetchCertifications()
      ]).then(() => {
        setLoading(false);
      }).catch(error => {
        console.error("Error al cargar los datos:", error);
        setError(t('workerDashboard.errorLoadingDashboard'));
        setLoading(false);
      });
    }
  }, [currentUser]);

  // Capturar ubicación actual y guardarla en Firestore (solo una vez al montar)
  useEffect(() => {
    if (!currentUser) return;
    if (!navigator.geolocation) return;
    setLocationCapturing(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          const timestamp = new Date().toISOString();
          const db = getFirestore();
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, {
            lastKnownLocation: { lat, lon, timestamp }
          });
          setLocationSaved(true);
        } finally {
          setLocationCapturing(false);
        }
      },
      () => {
        setLocationSaved(false);
        setLocationCapturing(false);
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  }, [currentUser]);

  // Detectar idioma del usuario y cambiarlo en i18n
  useEffect(() => {
    if (userData && userData.language && i18n.language !== userData.language) {
      i18n.changeLanguage(userData.language);
    }
  }, [userData, i18n]);

  // Recargar certificaciones cuando cambie el idioma
  useEffect(() => {
    if (currentUser && userData?.certifications) {
      fetchCertifications();
    }
  }, [i18n.language, currentUser]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    
    // Solo configurar notificaciones si el usuario es worker
    if (userData?.role !== 'worker') {
      console.log('[WorkerDashboard] User is not a worker, skipping notifications setup');
      return;
    }
    
    try {
      const notifRef = collection(db, 'notifications', currentUser.uid, 'notifications');
      // Cargar todas las notificaciones, no solo las no leídas
      const q = query(notifRef, orderBy('createdAt', 'desc'));
      const unsub = onSnapshot(q, snap => {
        const notifs: any[] = snap.docs.map(d => {
          const data = d.data() as any;
          return { ...data, id: d.id };
        });
        console.log('Notificaciones Firestore:', JSON.stringify(notifs, null, 2));
        setNotifications(notifs);
      }, (error) => {
        console.error('[WorkerDashboard] Error in notifications listener:', error);
        // Si hay error de permisos, no mostrar notificaciones
        if (error.code === 'permission-denied') {
          console.log('[WorkerDashboard] Permission denied for notifications, user may not be a worker');
        }
      });
      return () => unsub();
    } catch (error) {
      console.error('[WorkerDashboard] Error setting up notifications listener:', error);
    }
  }, [currentUser?.uid, userData?.role]);

  // Función para calcular la distancia entre dos coordenadas (Haversine)
  function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
    const toRad = (x: number) => x * Math.PI / 180;
    const R = 6371; // Radio de la tierra en km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Tarjeta visual de solicitud de asignación
  const AssignmentCard = ({ req, onAccept, onReject, onCounter, counterValue, setCounterValue }: any) => {
    const [showModal, setShowModal] = useState(false);
    const [showCounter, setShowCounter] = useState(false);
    const [currentPosition, setCurrentPosition] = useState<{lat: number, lon: number} | null>(null);
    const [modalDistance, setModalDistance] = useState<string | null>(null);
    const [distanceKm, setDistanceKm] = useState<string | null>(null);
    const [localCounterValue, setLocalCounterValue] = useState(counterValue);
    const [commission, setCommission] = useState<number | null>(null);
    const [commissionAmount, setCommissionAmount] = useState<number>(0);
    const [netAmount, setNetAmount] = useState<number>(0);
    const [businessLogo, setBusinessLogo] = useState<string | null>(null);
    const [businessName, setBusinessName] = useState<string | null>(null);
    const [businessType, setBusinessType] = useState<string | null>(null);

    // Calcular distancia para la tarjeta (solo una vez al montar o cuando cambian las props relevantes)
    useEffect(() => {
      let reqLat = Number((req.location && req.location.latitude) || req.latitude);
      let reqLon = Number((req.location && req.location.longitude) || req.longitude);
      let userLat: number | null = null;
      let userLon: number | null = null;
      if (userData?.lastKnownLocation && typeof userData.lastKnownLocation === 'object' && 'lat' in userData.lastKnownLocation && 'lon' in userData.lastKnownLocation) {
        userLat = Number(userData.lastKnownLocation.lat);
        userLon = Number(userData.lastKnownLocation.lon);
        if (isNaN(userLat)) userLat = null;
        if (isNaN(userLon)) userLon = null;
      }
      if (!isNaN(reqLat) && !isNaN(reqLon) && userLat !== null && userLon !== null) {
        const dist = haversineDistance(userLat, userLon, reqLat, reqLon);
        setDistanceKm(dist.toFixed(2));
      } else {
        setDistanceKm(null);
      }
      // eslint-disable-next-line
    }, [req, userData]);

    // Obtener ubicación actual del worker al abrir el modal
    useEffect(() => {
      if (!showModal) return;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setCurrentPosition({ lat: pos.coords.latitude, lon: pos.coords.longitude });
          },
          () => {
            setCurrentPosition(null);
          },
          { enableHighAccuracy: true, timeout: 5000 }
        );
      }
    }, [showModal]);

    // Calcular distancia en el modal (ubicación actual si existe, si no perfil)
    useEffect(() => {
      if (!showModal) return;
      let reqLat = Number((req.location && req.location.latitude) || req.latitude);
      let reqLon = Number((req.location && req.location.longitude) || req.longitude);
      let userLat: number | null = null;
      let userLon: number | null = null;
      if (currentPosition) {
        userLat = currentPosition.lat;
        userLon = currentPosition.lon;
      } else if (userData?.lastKnownLocation && typeof userData.lastKnownLocation === 'object' && 'lat' in userData.lastKnownLocation && 'lon' in userData.lastKnownLocation) {
        userLat = Number(userData.lastKnownLocation.lat);
        userLon = Number(userData.lastKnownLocation.lon);
        if (isNaN(userLat)) userLat = null;
        if (isNaN(userLon)) userLon = null;
      }
      if (!isNaN(reqLat) && !isNaN(reqLon) && userLat !== null && userLon !== null) {
        const dist = haversineDistance(userLat, userLon, reqLat, reqLon);
        setModalDistance(dist.toFixed(2));
      } else {
        setModalDistance(null);
      }
      // eslint-disable-next-line
    }, [showModal, currentPosition, req, userData]);

    useEffect(() => {
      const fetchCommission = async () => {
        try {
          const settingsDoc = await getDoc(doc(db, 'site', 'settings'));
          console.log('[COMMISSION] userData:', userData);
          console.log('[COMMISSION] settingsDoc:', settingsDoc.exists() ? settingsDoc.data() : null);
          let percent = 0;
          if (settingsDoc.exists()) {
            const data = settingsDoc.data();
            const com = data.comissions || {};
            if (userData?.role === 'worker') {
              percent = parseFloat(com.workerComission || '0');
              console.log('[COMMISSION] workerComission:', com.workerComission, 'percent:', percent);
            } else if (userData?.role === 'premiumWorker') {
              percent = parseFloat(com.premiumWorkerComission || '0');
              console.log('[COMMISSION] premiumWorkerComission:', com.premiumWorkerComission, 'percent:', percent);
            } else if (userData?.role === 'specialWorker') {
              percent = parseFloat(com.specialWorkerComission || '0');
              console.log('[COMMISSION] specialWorkerComission:', com.specialWorkerComission, 'percent:', percent);
            } else {
              console.log('[COMMISSION] No matching role:', userData?.role);
            }
            setCommission(percent);
            const total = req.total || 0;
            const fee = Math.round((total * percent) / 100);
            setCommissionAmount(fee);
            setNetAmount(total - fee);
            console.log('[COMMISSION] total:', total, 'fee:', fee, 'net:', total - fee);
          }
        } catch (e) {
          setCommission(null);
          setCommissionAmount(0);
          setNetAmount(req.total || 0);
          console.error('[COMMISSION] Error:', e);
        }
      };
      fetchCommission();
    }, [req.total, userData]);

    useEffect(() => {
      const fetchBusiness = async () => {
        if (!req.businessId) return;
        try {
          const businessDoc = await getDoc(doc(db, 'businesses', req.businessId));
          if (businessDoc.exists()) {
            const data = businessDoc.data();
            setBusinessLogo(data.logo || null);
            setBusinessName(data.companyName || data.name || null);
            setBusinessType(data.companyType || null);
          } else {
            // console.log('[BUSINESS] businessDoc does not exist for', req.businessId);
          }
        } catch (e) {
          setBusinessLogo(null);
          setBusinessName(null);
          setBusinessType(null);
          console.error('[BUSINESS] error:', e);
        }
      };
      fetchBusiness();
    }, [req.businessId]);

    useEffect(() => { setLocalCounterValue(counterValue); }, [counterValue]);

    return (
      <>
        <div
          className="relative group border-none rounded-3xl p-4 flex flex-col bg-white shadow-2xl border border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:z-10 hover:-translate-y-1 hover:rotate-1 mb-4 animate-fadeIn"
          style={{ minWidth: 0 }}
        >
          {/* Logo en la esquina superior derecha */}
          {businessLogo && (
            <img src={businessLogo} alt="Business Logo" className="absolute top-4 right-4 h-10 w-10 rounded-xl bg-gray-100 border border-gray-200 shadow" />
          )}
          <div className="flex items-center gap-4 mb-2">
            <span className="text-2xl text-red-500 bg-red-50 rounded-full p-2 shadow-md">
              <HiOutlineDocumentText />
            </span>
            <div className="flex flex-col gap-1 min-w-0 w-full">
              <div className="font-bold text-base text-gray-800 mb-1 whitespace-pre-line break-words">{req.taskTitle}</div>
              {/* Requested by debajo del título, con tipo de empresa */}
              {businessName && (
                <div className="text-xs text-gray-400 mb-1">
                  Requested by: <span className="text-gray-600">{businessName}</span>
                  {businessType && (
                    <span className="text-gray-400 ml-2">({businessType})</span>
                  )}
                </div>
              )}
              {req.taskDescription && (
                <div className="text-gray-700 text-sm mb-1 whitespace-pre-line break-words">{req.taskDescription}</div>
              )}
              {/* Fecha y hora del trabajo */}
              {req.startDate && (
                <div className="mb-2 text-gray-700 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    📅 {typeof req.startDate === 'string' 
                      ? new Date(req.startDate).toLocaleDateString(i18n.language, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : new Date(req.startDate.seconds * 1000).toLocaleDateString(i18n.language, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                    }
                  </span>
                  {req.startTime && (
                    <span className="text-sm font-medium text-gray-600">
                      🕐 {req.startTime}
                    </span>
                  )}
                </div>
              )}
              {((req.city || (req.location && req.location.city)) || (req.state || (req.location && req.location.state))) && (
                <div className="mb-2 text-gray-700 flex items-center gap-2">
                  {req.city || (req.location && req.location.city) || ''}
                  {(req.city || (req.location && req.location.city)) && (req.state || (req.location && req.location.state)) ? ', ' : ''}
                  {req.state || (req.location && req.location.state) || ''}
                  {distanceKm && (
                    <span className="ml-2 text-gray-500 font-semibold flex items-center gap-1">
                      <FaMapMarkerAlt className="inline-block text-gray-400" />
                      {t('projects.assignment.distance', { distance: distanceKm })}
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-1">
                {req.city && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700" title={req.city}>{req.city}</span>
                )}
                {req.state && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700" title={req.state}>{req.state}</span>
                )}
                {req.duration !== undefined && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                    {t('projects.assignment.duration')}: {req.duration} {req.priceUnit === 'hour' ? t('projects.assignment.hours') : t('projects.assignment.days')}
                  </span>
                )}
                {req.price !== undefined && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    {t('projects.assignment.amount')}: {req.price} {req.priceUnit === 'hour' ? t('projects.assignment.perHour') : t('projects.assignment.perDay')}
                  </span>
                )}
                {req.status === 'counter' && req.lastCounterBy === 'business' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 animate-pulse">
                    🔄 Contraoferta pendiente
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            className="mt-4 w-full py-2 px-4 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold shadow-lg text-base tracking-wide transition-all duration-200 hover:scale-105 hover:from-pink-500 hover:to-red-500 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
            onClick={() => {
              console.log('Modal clicked, req:', req);
              setShowModal(true);
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <FaEye className="inline-block text-lg" />
              {t('common.view')}
            </span>
          </button>
        </div>
        {showModal && ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-3xl w-full relative">
              <button className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-red-600" onClick={() => setShowModal(false)}>&times;</button>
              <h2 className="text-2xl font-bold text-red-600 mb-4 flex items-center gap-2">
                <HiOutlineDocumentText className="inline-block text-red-400" />
                {req.taskTitle || t('projects.assignment.title')}
              </h2>
              <div className="flex flex-col md:flex-row gap-8 mb-4">
                {/* Sección 1 */}
                <div className="flex-1 space-y-3 border-r border-gray-200 pr-6">
                  <div>
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.kanban.taskDesc') || 'Descripción de la tarea'}</div>
                    <div className="text-gray-800 text-base font-medium whitespace-pre-line break-words">{req.taskDescription}</div>
                  </div>
                  {req.status && (
                    <div>
                      <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.assignment.status') || 'Estado'}</div>
                      <div className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800 shadow-sm">{t(`projects.assignment.statuses.${req.status}`)}</div>
                    </div>
                  )}
                  {(req.location?.address || req.location?.city || req.location?.state || req.location?.country) && (
                    <div>
                      <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.assignment.location') || 'Ubicación'}</div>
                      <div className="text-gray-800 text-sm">{req.location?.address}</div>
                      <div className="text-gray-600 text-xs">{[req.location?.city, req.location?.state, req.location?.country].filter(Boolean).join(', ')}</div>
                    </div>
                  )}
                  {modalDistance && (
                    <div className="flex items-center gap-2 text-gray-500 text-xs mt-2">
                      <FaMapMarkerAlt className="inline-block text-gray-400" />
                      {t('projects.assignment.distance', { distance: modalDistance })}
                    </div>
                  )}
                </div>
                {/* Sección 2 */}
                <div className="flex-1 space-y-3 pl-0 md:pl-6">
                  {req.price !== undefined && (
                    <div>
                      <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.assignment.amount') || 'Monto'}</div>
                      <div className="text-gray-800 text-base font-medium">{req.price} {req.priceUnit === 'hour' ? t('projects.assignment.perHour') : t('projects.assignment.perDay')}</div>
                    </div>
                  )}
                  {req.total !== undefined && (
                    <div>
                      <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.assignment.total') || 'Total'}</div>
                      <div className="text-gray-800 text-base font-medium">${req.total}</div>
                      {commission !== null && (
                        <div className="mt-2">
                          <div className="text-gray-500 text-xs font-semibold mb-1">{t('projects.assignment.feeLabel', 'Service fee')}: {commission}%</div>
                          <div className="text-gray-700 text-sm">- ${commissionAmount} {t('projects.assignment.fee', 'fee')}</div>
                          <div className="text-gray-800 text-base font-semibold">{t('projects.assignment.netLabel', 'Net to receive')}: ${netAmount}</div>
                          <div className="text-xs text-gray-400 underline mt-1">{t('projects.assignment.feeInfo', 'How our costs work')}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {req.status === 'counter' && req.lastCounterBy === 'business' && req.counterPrice && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="text-blue-800 text-sm font-semibold mb-2">🔄 Contraoferta del Business</div>
                      <div className="text-blue-700 text-sm">
                        <div>Precio: ${req.counterPrice} {req.priceUnit === 'hour' ? '/hora' : '/día'}</div>
                        <div>Total: ${req.total}</div>
                      </div>
                    </div>
                  )}
                  {req.duration !== undefined && (
                    <div>
                      <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.assignment.duration') || 'Duración'}</div>
                      <div className="text-gray-800 text-base font-medium">{req.duration} {req.priceUnit === 'hour' ? t('projects.assignment.hours') : t('projects.assignment.days')}</div>
                    </div>
                  )}
                  {req.startDate && (
                    <div>
                      <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('projects.assignment.startDate') || 'Fecha y hora de inicio'}</div>
                      <div className="text-gray-800 text-base font-medium">
                        {typeof req.startDate === 'string' 
                          ? new Date(req.startDate).toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) + (req.startTime ? ` ${req.startTime}` : '')
                          : new Date(req.startDate.seconds * 1000).toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) + (req.startTime ? ` ${req.startTime}` : '')
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Acciones */}
              <div className="flex flex-col gap-4 mt-8">
                {(req.status === 'pending' || (req.status === 'counter' && req.lastCounterBy === 'business')) && (() => {
                  console.log('Showing action buttons, req.status:', req.status, 'req.lastCounterBy:', req.lastCounterBy);
                  return true;
                })() && (
                  <div className="flex gap-4">
                    <button
                      onClick={onAccept}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-lg shadow-lg transition-all duration-200 hover:scale-105 hover:from-green-600 hover:to-green-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
                    >
                      <FaCheck className="text-xl" />
                      {t('common.accept')}
                    </button>
                    <button
                      onClick={onReject}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold text-lg shadow-lg transition-all duration-200 hover:scale-105 hover:from-pink-600 hover:to-red-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                    >
                      <FaTimes className="text-xl" />
                      {t('common.reject')}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setShowCounter(!showCounter)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-bold text-lg shadow-lg transition-all duration-200 hover:scale-105 hover:from-yellow-500 hover:to-yellow-600 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2"
                >
                  <FaExchangeAlt className="text-xl" />
                  {t('projects.assignment.counter')}
                </button>
                {showCounter && (
                  <div className="flex flex-col md:flex-row gap-2 w-full mt-2 items-center">
                    <div className="w-full flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-600 mb-1" htmlFor={`counteroffer-input-${req.id}`}>
                        {t('projects.assignment.counterLabel', 'Tu contraoferta')} {req.priceUnit === 'hour' ? t('projects.assignment.perHour', '/hora') : t('projects.assignment.perDay', '/día')}
                      </label>
                      <input
                        id={`counteroffer-input-${req.id}`}
                        type="number"
                        className="input input-bordered flex-1 py-3 text-lg rounded-xl border-2 border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                        value={localCounterValue}
                        onChange={e => {
                          setLocalCounterValue(e.target.value);
                          setCounterValue((prev: any) => ({ ...prev, [req.id]: e.target.value }));
                        }}
                        placeholder={t('projects.assignment.amount')}
                        min={0}
                        step="any"
                      />
                    </div>
                    <button
                      onClick={onCounter}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-lg shadow-lg transition-all duration-200 hover:scale-105 hover:from-blue-600 hover:to-blue-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                    >
                      <FaCheck className="text-xl" />
                      {t('common.submit')}
                    </button>
                  </div>
                )}
                {/* Desglose de montos para contraoferta */}
                {showCounter && (
                  (() => {
                    // Calcular valores en tiempo real según el input
                    const precio = Number(localCounterValue) || 0;
                    const duracion = Number(req.duration) || 0;
                    const total = precio * duracion;
                    const fee = commission !== null ? Math.round((total * commission) / 100) : 0;
                    const neto = total - fee;
                    if (!precio || !duracion) return null;
                    return (
                      <div className="mt-4 space-y-1">
                        <div>{t('projects.assignment.totalLabel', 'Total')}: ${total}</div>
                        <div>{t('projects.assignment.feeLabelShort', 'Service fee')}: -${fee}</div>
                        <div>{t('projects.assignment.netLabelShort', 'Net to receive')}: ${neto}</div>
                        <div className="underline cursor-pointer text-gray-600">{t('projects.assignment.feeInfoShort', 'How our costs work')}</div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };

  const AssignmentRequests = () => {
    const { currentUser } = useAuth();
    const [requests, setRequests] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [counterPrice, setCounterPrice] = useState<{[id: string]: string}>({});
    const db = getFirestore();

    useEffect(() => {
      if (!currentUser?.uid) return;
      const q = query(
        collection(db, 'assignmentRequests'),
        where('workerId', '==', currentUser.uid),
        where('status', 'in', ['pending', 'counter'])
      );
      const unsub = onSnapshot(q, snap => {
        // Forzar tipado correcto para evitar errores de acceso a status y lastCounterBy
        const all = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        console.log('Solicitudes de asignación Firestore:', JSON.stringify(all, null, 2));
        setRequests(
          all.filter(r => r.status === 'pending' || (r.status === 'counter' && r.lastCounterBy === 'business'))
        );
        setLoading(false);
      });
      return () => unsub();
    }, [currentUser?.uid]);

    const handleAccept = async (req: any) => {
      try {
        // 1. Asegurar que tenemos projectId
        let projectId = req.projectId;
        if (!projectId) {
          // Buscar el assignmentRequest y extraer projectId
          const assignmentSnap = await getDoc(doc(db, 'assignmentRequests', req.id));
          if (assignmentSnap.exists()) {
            const data = assignmentSnap.data();
            projectId = data.projectId;
          }
        }
        // Si aún no hay projectId, buscarlo recorriendo los proyectos del business
        if (!projectId && req.businessId && req.taskId) {
          const projectsCol = collection(db, `projects/${req.businessId}/projects`);
          const projectsSnap = await getDocs(projectsCol);
          for (const projectDoc of projectsSnap.docs) {
            const projectIdCandidate = projectDoc.id;
            const taskRef = doc(db, `projects/${req.businessId}/projects/${projectIdCandidate}/tasks/${req.taskId}`);
            const taskSnap = await getDoc(taskRef);
            if (taskSnap.exists()) {
              projectId = projectIdCandidate;
              break;
            }
          }
        }
        if (!req.businessId || !projectId || !req.taskId || !req.workerId) {
          setToast({ message: 'Faltan datos para asignar la tarea. Contacta a soporte.', type: 'error' });
          return;
        }
        // 2. Actualizar estado de la solicitud
        await updateDoc(doc(db, 'assignmentRequests', req.id), { status: 'accepted', updatedAt: new Date().toISOString() });

        // 3. Asignar worker y guardar total en la tarea (subcolección)
        const taskRef = doc(db, `projects/${req.businessId}/projects/${projectId}/tasks/${req.taskId}`);
        const taskSnap = await getDoc(taskRef);
        if (!taskSnap.exists()) {
          setToast({ message: 'No se encontró la tarea para asignar el worker.', type: 'error' });
        } else {
          // Calcular fee y neto
          const settingsDoc = await getDoc(doc(db, 'site', 'settings'));
          let percent = 0;
          if (settingsDoc.exists()) {
            const com = settingsDoc.data().comissions || {};
            if (userData?.role === 'worker') {
              percent = parseFloat(com.workerComission || '0');
            } else if (userData?.role === 'premiumWorker') {
              percent = parseFloat(com.premiumWorkerComission || '0');
            } else if (userData?.role === 'specialWorker') {
              percent = parseFloat(com.specialWorkerComission || '0');
            }
          }
          const total = req.total || 0;
          const fee = Math.round((total * percent) / 100);
          const net = total - fee;
          try {
            await updateDoc(taskRef, {
              assignedTo: req.workerId,
              worker: req.workerId,
              finalTotal: total, // total = neto + fee
              finalPrice: req.price || null,
              finalPriceUnit: req.priceUnit || null,
              finalDuration: req.duration || null,
              fee,
              netAmount: net,
              assignmentRequestId: req.id,
              status: 'assigned',
            });
          } catch (err) {
            setToast({ message: 'Error actualizando la tarea: ' + ((err as any).message || err), type: 'error' });
          }
          // 4. Crear el pago pendiente en wallet
          try {
            const walletRef = doc(db, 'wallet', req.workerId);
            const walletSnap = await getDoc(walletRef);
            let disponible = 0;
            let retirado = 0;
            let pendiente = 0;
            if (walletSnap.exists()) {
              const data = walletSnap.data();
              disponible = data.disponible || 0;
              retirado = data.retirado || 0;
              pendiente = data.pendiente || 0;
            }
            pendiente += net;
            await updateDoc(walletRef, {
              disponible,
              retirado,
              pendiente
            }).catch(async (err) => {
              if ((err as any).code === 'not-found') {
                await setDoc(walletRef, {
                  disponible: 0,
                  retirado: 0,
                  pendiente: net
                });
              } else {
                throw err;
              }
            });
            await addDoc(collection(db, 'wallet', req.workerId, 'transacciones'), {
              tipo: 'pendiente_pago',
              monto: net,
              taskId: req.taskId,
              projectId,
              assignmentRequestId: req.id,
              businessId: req.businessId,
              taskTitle: req.taskTitle,
              fecha: new Date(),
              estado: 'pendiente',
              fee,
              feePercent: percent,
              total,
              startDate: req.startDate || null,
              duration: req.duration || null,
            });
          } catch (err) {
            setToast({ message: 'Error creando el pago pendiente en wallet: ' + ((err as any).message || err), type: 'error' });
          }
        }

        // 5. Notificar al business (oferta aceptada)
        await addDoc(collection(db, 'notifications', req.businessId, 'notifications'), {
          type: 'assignmentAccepted',
          messageKey: 'notifications.assignmentAccepted',
          messageParams: {
            taskTitle: req.taskTitle,
            businessId: req.businessId,
            workerId: req.workerId,
            price: req.price,
            counterPrice: req.counterPrice,
            total: req.total
          },
          assignmentRequestId: req.id,
          businessId: req.businessId,
          workerId: req.workerId,
          createdAt: new Date().toISOString(),
          read: false
        });

        // 6. Notificar al worker (nuevo trabajo asignado)
        await addDoc(collection(db, 'notifications', req.workerId, 'notifications'), {
          type: 'assignmentNewJob',
          messageKey: 'notifications.assignmentNewJob',
          messageParams: {
            taskTitle: req.taskTitle,
            businessId: req.businessId,
            workerId: req.workerId,
            price: req.price,
            counterPrice: req.counterPrice,
            total: req.total
          },
          assignmentRequestId: req.id,
          businessId: req.businessId,
          workerId: req.workerId,
          createdAt: new Date().toISOString(),
          read: false
        });

        // 7. Actualizar referencias en el usuario worker
        try {
          const userRef = doc(db, 'users', req.workerId);
          await updateDoc(userRef, {
            businessesAsignados: arrayUnion(req.businessId),
            proyectosAsignados: arrayUnion(projectId),
            tareasAsignadas: arrayUnion({ projectId, taskId: req.taskId, businessId: req.businessId })
          });
        } catch (err) {
          setToast({ message: 'Error actualizando referencias en el usuario: ' + ((err as any).message || err), type: 'error' });
        }

        setToast({ message: t('projects.assignment.statuses.accepted'), type: 'success' });
        setTimeout(() => setToast(null), 2000);
        setRequests(prev => prev.filter(r => r.id !== req.id));
      } catch (err) {
        setToast({ message: 'Error general al aceptar la oferta: ' + ((err as any).message || err), type: 'error' });
      }
    };

    const handleReject = async (req: any) => {
      await updateDoc(doc(db, 'assignmentRequests', req.id), { status: 'rejected', updatedAt: new Date().toISOString() });
      await addDoc(collection(db, 'notifications', req.businessId, 'notifications'), {
        type: 'assignmentResponse',
        messageKey: 'notifications.assignmentRejected',
        messageParams: {
          taskTitle: req.taskTitle,
          businessId: req.businessId,
          workerId: req.workerId,
          price: req.price,
          counterPrice: req.counterPrice,
          total: req.total
        },
        assignmentRequestId: req.id,
        businessId: req.businessId,
        workerId: req.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      setToast({ message: t('projects.assignment.statuses.rejected'), type: 'error' });
      setTimeout(() => setToast(null), 2000);
      setRequests(prev => prev.filter(r => r.id !== req.id));
    };

    const handleCounter = async (req: any) => {
      const price = Number(counterPrice[req.id]);
      if (!price) {
        setToast({ message: t('projects.assignment.counter') + ': ' + t('common.invalid'), type: 'error' });
        setTimeout(() => setToast(null), 2000);
        return;
      }
      const total = price * Number(req.duration);
      await updateDoc(doc(db, 'assignmentRequests', req.id), {
        status: 'counter',
        counterPrice: price,
        total: total,
        updatedAt: new Date().toISOString(),
        lastCounterBy: 'worker'
      });
      await addDoc(collection(db, 'notifications', req.businessId, 'notifications'), {
        type: 'assignmentResponse',
        messageKey: 'notifications.assignmentCounter',
        messageParams: {
          taskTitle: req.taskTitle,
          businessId: req.businessId,
          workerId: req.workerId,
          price: req.price,
          counterPrice: price,
          total: total,
          duration: req.duration,
          priceUnit: req.priceUnit
        },
        assignmentRequestId: req.id,
        businessId: req.businessId,
        workerId: req.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      // 5. Crear documento de pago en la wallet del worker (igual que en handleAccept)
      try {
        const settingsDoc = await getDoc(doc(db, 'site', 'settings'));
        let percent = 0;
        if (settingsDoc.exists()) {
          const com = settingsDoc.data().comissions || {};
          if (userData?.role === 'worker') {
            percent = parseFloat(com.workerComission || '0');
          } else if (userData?.role === 'premiumWorker') {
            percent = parseFloat(com.premiumWorkerComission || '0');
          } else if (userData?.role === 'specialWorker') {
            percent = parseFloat(com.specialWorkerComission || '0');
          }
        }
        const total = price * Number(req.duration);
        const fee = Math.round((total * percent) / 100);
        const net = total - fee;
        await addDoc(collection(db, 'users', req.workerId, 'wallet'), {
          ...req,
          fee,
          feePercent: percent,
          netAmount: net,
          createdAt: new Date().toISOString(),
          status: 'pending',
          type: 'assignment',
        });
      } catch (e) {
        console.error('Error creando documento de pago en wallet:', e);
      }
      setToast({ message: t('projects.assignment.counter') + ' ' + t('common.sent'), type: 'success' });
      setTimeout(() => setToast(null), 2000);
      setCounterPrice(prev => ({ ...prev, [req.id]: '' }));
      setRequests(prev => prev.filter(r => r.id !== req.id));
    };

    if (loading) return <div className="text-center text-gray-500 p-8 animate-fadeIn">{t('common.loading')}</div>;
    if (requests.length === 0) return <div className="text-center text-gray-500 p-8 animate-fadeIn">{t('projects.assignment.noRequests') || 'No tienes solicitudes de asignación.'}</div>;

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-red-600 mb-6 animate-fadeIn">{t('projects.assignment.title') || 'Solicitudes de asignación'}</h2>
        {requests.map(req => (
          <AssignmentCard
            key={req.id}
            req={req}
            onAccept={() => handleAccept(req)}
            onReject={() => handleReject(req)}
            onCounter={() => handleCounter(req)}
            counterValue={counterPrice[req.id] || ''}
            setCounterValue={setCounterPrice}
          />
        ))}
      </div>
    );
  };

  // NUEVO: Obtener proyectos donde el worker es staff
  useEffect(() => {
    const fetchAssignedProjectsAndTasks = async () => {
      if (!userData) return;
      const db = getFirestore();
      let allProjects: any[] = [];
      let allTasks: any[] = [];
      // Cargar proyectos asignados
      if (Array.isArray(userData.proyectosAsignados)) {
        for (const projectId of userData.proyectosAsignados) {
          // Buscar el businessId correspondiente (de tareasAsignadas o businessesAsignados)
          let businessId = null;
          if (Array.isArray(userData.tareasAsignadas)) {
            const tarea = userData.tareasAsignadas.find((t: any) => t.projectId === projectId);
            if (tarea) businessId = tarea.businessId;
          }
          if (!businessId && Array.isArray(userData.businessesAsignados) && userData.businessesAsignados.length > 0) {
            businessId = userData.businessesAsignados[0]; // fallback
          }
          if (businessId) {
            try {
              const projectRef = doc(db, `projects/${businessId}/projects/${projectId}`);
              const projectSnap = await getDoc(projectRef);
              if (projectSnap.exists()) {
                allProjects.push({ id: projectId, businessId, ...(projectSnap.data() as any) });
              }
            } catch (e) { /* ignorar errores individuales */ }
          }
        }
      }
      // Cargar tareas asignadas
      if (Array.isArray(userData.tareasAsignadas)) {
        for (const tarea of userData.tareasAsignadas) {
          const { businessId, projectId, taskId } = tarea;
          if (businessId && projectId && taskId) {
            try {
              const taskRef = doc(db, `projects/${businessId}/projects/${projectId}/tasks/${taskId}`);
              const taskSnap = await getDoc(taskRef);
              if (taskSnap.exists()) {
                const projectName = allProjects.find(p => p.id === projectId)?.name || '';
                allTasks.push({ id: taskId, ...taskSnap.data(), projectName, projectId, businessId });
              }
            } catch (e) { /* ignorar errores individuales */ }
          }
        }
      }
      setActiveProjects(allProjects);
      setAssignedTasks(allTasks);
    };
    fetchAssignedProjectsAndTasks();
  }, [userData]);

  // NUEVO: Componente para mostrar certificaciones
  const CertificationsCard = () => (
    <section className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('workerDashboard.certifications')}</h2>
      {certifications.length === 0 ? (
        <div className="text-gray-500 text-center">{t('workerDashboard.noCertifications')}</div>
      ) : (
        <ul className="space-y-3">
          {certifications.map(cert => (
            <li key={cert.certificationId} className={`rounded-xl p-4 flex flex-col gap-1 shadow-sm border animate-fadeIn ${
              cert.status === 'approved' ? 'bg-green-50 border-green-200' :
              cert.status === 'rejected' ? 'bg-red-50 border-red-200' :
              'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800">{cert.name}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  cert.status === 'approved' ? 'bg-green-100 text-green-800' :
                  cert.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {cert.status === 'approved' ? t('workerDashboard.certificationApproved') :
                   cert.status === 'rejected' ? t('workerDashboard.certificationRejected') :
                   t('workerDashboard.certificationPending')}
                </span>
              </div>
              <span className="text-xs text-gray-500">{t('workerDashboard.issuer')}: {cert.issuer}</span>
              <span className="text-xs text-gray-500">{t('workerDashboard.issueDate')}: {cert.date}</span>
              <a href={cert.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-red-600 hover:underline">{t('workerDashboard.viewCertification')}</a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  // NUEVO: Componente para mostrar proyectos activos
  const ActiveProjects = () => (
    <section className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg h-full relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
      <h2 className="text-2xl font-bold text-red-600 mb-6 animate-fadeIn">{t('workerDashboard.currentProjects')}</h2>
      {(!userData?.proyectosAsignados || userData.proyectosAsignados.length === 0) ? (
        <div className="text-gray-500 text-center">{t('workerDashboard.noProjects')}</div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {activeProjects.filter(project => userData.proyectosAsignados.includes(project.id)).map(project => {
            // Obtener tareas asignadas al worker en este proyecto
            const myTasksInProject = project.tasks?.filter((t: any) => {
              if (t.worker) {
                if (Array.isArray(t.worker)) {
                  return t.worker.includes(currentUser?.uid);
                } else {
                  return t.worker === currentUser?.uid;
                }
              }
              return t.assignedTo === currentUser?.uid;
            }) || [];
            
            // Calcular el monto total que recibirá el worker por sus tareas asignadas
            const myTotalEarnings = myTasksInProject.reduce((total: number, task: any) => {
              return total + (task.finalTotal || 0);
            }, 0);
            
            // Calcular el progreso basado solo en las tareas asignadas al worker
            const myTasksProgress = myTasksInProject.length > 0 
              ? myTasksInProject.reduce((total: number, task: any) => {
                  return total + (task.progress || 0);
                }, 0) / myTasksInProject.length
              : 0;
            
            return (
              <div 
                key={project.id} 
                className="bg-gray-50 rounded-2xl p-6 shadow border border-gray-100 flex flex-col gap-3 animate-fadeIn hover:scale-105 hover:shadow-lg transition-all duration-200 cursor-pointer"
                onClick={() => navigate(`/worker-projects/${project.id}`)}
                tabIndex={0}
                role="button"
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/worker-projects/${project.id}`); }}
              >
                <div className="flex items-start justify-between">
                  <div className="font-bold text-lg text-gray-900">{project.name}</div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    project.status === 'active' ? 'bg-green-100 text-green-800' :
                    project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {project.status === 'active' ? t('workerDashboard.projectActive') :
                     project.status === 'completed' ? t('workerDashboard.projectCompleted') :
                     project.status}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  {project.startDate && (
                    <span>📅 {new Date(project.startDate.seconds ? project.startDate.seconds * 1000 : project.startDate).toLocaleDateString(i18n.language)}</span>
                  )}
                  {project.endDate && (
                    <span>🏁 {new Date(project.endDate.seconds ? project.endDate.seconds * 1000 : project.endDate).toLocaleDateString(i18n.language)}</span>
                  )}
                  {myTotalEarnings > 0 && (
                    <span>💰 {t('workerDashboard.myPayment')}: ${myTotalEarnings}</span>
                  )}
                </div>
                
                {project.description && (
                  <div className="text-xs text-gray-600">{project.description}</div>
                )}
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">📋 {t('workerDashboard.myTasks')}: <span className="font-semibold text-red-600">{myTasksInProject.length}</span></span>
                  <span className="text-gray-500">📊 {t('workerDashboard.myProgress')}: <span className="font-semibold text-red-600">{Math.round(myTasksProgress)}%</span></span>
                </div>
                
                {/* Barra de progreso de las tareas del worker */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round(myTasksProgress)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  // NUEVO: Componente para mostrar tareas asignadas
  const AssignedTasks = () => (
    <section className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg h-full relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
      <h2 className="text-2xl font-bold text-red-600 mb-6 animate-fadeIn">{t('workerDashboard.assignedTasksTitle')}</h2>
      {(!userData?.tareasAsignadas || userData.tareasAsignadas.length === 0) ? (
        <div className="text-gray-500 text-center">{t('workerDashboard.noAssignedTasks')}</div>
      ) : (
        <ul className="space-y-4">
          {assignedTasks.filter(task => userData.tareasAsignadas.some((t: any) => t.taskId === task.id && t.projectId === task.projectId)).map((task, idx) => (
            <li key={task.id || idx} className="bg-gray-50 rounded-xl p-4 shadow border border-gray-100 animate-fadeIn flex flex-col gap-2 hover:shadow-lg transition-all duration-200">
              <div className="flex items-start justify-between">
                <span className="font-semibold text-gray-800 text-sm">{task.title}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  task.columnId === 'new' ? 'bg-blue-100 text-blue-800' :
                  task.columnId === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  task.columnId === 'done' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {task.columnId === 'new' ? t('workerDashboard.taskStatusNew') :
                   task.columnId === 'in_progress' ? t('workerDashboard.taskStatusInProgress') :
                   task.columnId === 'done' ? t('workerDashboard.taskStatusDone') :
                   task.columnId}
                </span>
              </div>
              
              {task.description && (
                <span className="text-xs text-gray-600">{task.description}</span>
              )}
              
              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                {task.startDate && (
                  <span>📅 {(() => {
                    let dateObj = null;
                    if (typeof task.startDate === 'string' || typeof task.startDate === 'number') {
                      dateObj = new Date(task.startDate);
                    } else if (task.startDate && typeof task.startDate === 'object' && 'seconds' in task.startDate) {
                      dateObj = new Date(task.startDate.seconds * 1000);
                    }
                    return dateObj && !isNaN(dateObj.getTime())
                      ? dateObj.toLocaleDateString(i18n.language)
                      : '-';
                  })()}</span>
                )}
                {task.startTime && (
                  <span>🕐 {task.startTime}</span>
                )}
                {task.duration && (
                  <span>⏱️ {task.duration} {task.durationUnit === 'days' ? t('workerDashboard.days') : t('workerDashboard.hours')}</span>
                )}
                {task.finalPrice && (
                  <span>💰 ${task.finalPrice} {task.finalPriceUnit === 'hour' ? t('workerDashboard.perHour') : t('workerDashboard.perDay')}</span>
                )}
                {task.finalTotal && (
                  <span>💵 {t('workerDashboard.total')}: ${task.finalTotal}</span>
                )}
              </div>
              
              <div className="text-xs text-gray-500">
                📋 {t('workerDashboard.project')}: {task.projectName}
              </div>
              
              {/* Progreso del checklist si está habilitado */}
              {task.checklistEnabled && task.checklist && task.checklist.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>{t('workerDashboard.checklist')}</span>
                    <span>{task.progress || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-red-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${task.progress || 0}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  // Nueva sección: Empresas asignadas
  const AssignedBusinesses = () => (
    <section className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg h-full relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10">
      <h2 className="text-2xl font-bold text-red-600 mb-6 animate-fadeIn">{t('workerDashboard.assignedBusinesses', 'Assigned Businesses')}</h2>
      {assignedBusinesses.length === 0 ? (
        <div className="text-gray-500 text-center">{t('workerDashboard.noBusinesses', 'No assigned businesses')}</div>
      ) : (
        <ul className="space-y-4">
          {assignedBusinesses.map(business => (
            <li key={business.id} className="bg-gray-50 rounded-xl p-4 shadow border border-gray-100 animate-fadeIn flex flex-col gap-2 hover:shadow-lg transition-all duration-200">
              <div className="font-semibold text-gray-800 text-base">{business.companyName || business.name || business.id}</div>
              {business.email && <div className="text-xs text-gray-600">{business.email}</div>}
              {business.status && <div className="text-xs text-gray-500">{typeof t('business.statuses.' + business.status) === 'string' ? t('business.statuses.' + business.status) : business.status}</div>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  // Tarjeta de Onboarding
  const OnBoardingCard = () => {
    if (!userData) return null;
    const completed = Array.isArray(userData.completedSteps) ? userData.completedSteps : [];
    const total = ONBOARDING_STEPS.length;
    const done = completed.length;
    const percent = Math.round((done / total) * 100);
    const missing = ONBOARDING_STEPS.filter(step => !completed.includes(step.id));

    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-2xl p-6 mb-8 shadow flex flex-col md:flex-row items-center gap-6 animate-fadeIn">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-yellow-700 mb-2 flex items-center gap-2">
            <FaCheck className="text-yellow-500" />
            {t('workerDashboard.onboardingTitle')}
          </h2>
          <p className="text-yellow-800 mb-2">{t('workerDashboard.onboardingDescription')}</p>
          <div className="flex items-center gap-4 mb-2">
            <div className="w-40 bg-yellow-200 rounded-full h-3">
              <div className="bg-yellow-500 h-3 rounded-full transition-all duration-300" style={{ width: `${percent}%` }}></div>
            </div>
            <span className="text-yellow-700 font-semibold">{t('workerDashboard.onboardingProgress', { percent })}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {ONBOARDING_STEPS.map(step => (
              <span key={step.id} className={`px-3 py-1 rounded-full text-xs font-medium ${completed.includes(step.id) ? 'bg-green-100 text-green-700 line-through' : 'bg-yellow-100 text-yellow-700'}`}>
                {step.label}
              </span>
            ))}
          </div>
          {missing.length > 0 && (
            <div className="mt-4 text-yellow-800 text-sm">
              {t('workerDashboard.onboardingPendingSteps', { steps: missing.map(s => s.label).join(', ') })}
            </div>
          )}
        </div>
        <button
          onClick={() => navigate('/onboarding')}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200 mt-4 md:mt-0"
        >
          {t('workerDashboard.onboardingGoTo')}
        </button>
      </div>
    );
  };

  // En el WorkerDashboard, después de cargar userData, cargar los datos de empresas asignadas
  useEffect(() => {
    const fetchAssignedBusinesses = async () => {
      if (!userData?.businessesAsignados || !Array.isArray(userData.businessesAsignados)) {
        setAssignedBusinesses([]);
        return;
      }
      const db = getFirestore();
      const businesses = [];
      for (const businessId of userData.businessesAsignados) {
        try {
          const businessDoc = await getDoc(doc(db, 'businesses', businessId));
          if (businessDoc.exists()) {
            businesses.push({ id: businessId, ...businessDoc.data() });
          }
        } catch (e) {
          // Ignorar errores individuales
        }
      }
      setAssignedBusinesses(businesses);
    };
    fetchAssignedBusinesses();
  }, [userData]);

  if (loading) {
    return (
      <div className="p-8 w-full min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] relative overflow-hidden">
        <div className="text-center p-8">{t('workerDashboard.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 w-full min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] relative overflow-hidden">
        <div className="text-red-600 text-center p-8">{error}</div>
      </div>
    );
  }

  // Mostrar la tarjeta de onboarding si falta completar
  const showOnboarding = (userData?.role === 'worker' || userData?.role === 'User') && (
    !userData?.onBoarding ||
    (Array.isArray(userData?.completedSteps) && userData.completedSteps.length < ONBOARDING_STEPS.length)
  );

  return (
    <div className="p-8 w-full min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] relative overflow-hidden">
      {/* Toast de alerta */}
      {toast && toast.type === 'error' && (
        <div className={`fixed top-6 right-6 z-[9999] px-6 py-4 rounded-xl shadow-lg text-white text-lg font-semibold transition-all duration-500 animate-fadeIn bg-black`}>
          {toast.message}
        </div>
      )}
      {/* Tarjeta de Onboarding */}
      {showOnboarding && <OnBoardingCard />}

      <header className="grid grid-cols-1 md:grid-cols-3 items-center mb-12 p-10 bg-white rounded-3xl shadow-2xl border border-white/40 w-full relative overflow-hidden gap-8 before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-1.5 before:bg-gradient-to-r before:from-red-600 before:to-pink-500 before:rounded-b-3xl before:z-10">
        <div className="relative flex items-center gap-6 justify-self-start">
          <button 
            ref={bellRef}
            onClick={() => setNotificationsModalOpen(true)} 
            aria-label={t('workerDashboard.notifications')}
            className="bg-transparent border-none cursor-pointer text-2xl text-red-600 mr-3 flex items-center transition-colors duration-200 hover:text-pink-500 relative"
          >
            <FaBell />
            {notifications.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full text-xs font-bold px-1.5 py-0.5 min-w-[20px] text-center border-2 border-white shadow-lg animate-bounce">
                {notifications.length}
              </span>
            )}
          </button>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 p-2 bg-white rounded-xl shadow-sm">
              <span className="text-sm text-gray-800 font-semibold">
                {t('workerDashboard.wallet')}:
              </span>
              <span className="text-lg text-red-600 font-bold">
                {userData?.availableFunds ? 
                  new Intl.NumberFormat(i18n.language, { 
                    currency: 'USD' 
                  }).format(userData.availableFunds) : 
                  '$0.00'
                }
              </span>
            </div>
            {/* Botón 'Ver Mis Proyectos' eliminado */}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 text-center justify-self-center">
          <div className="flex items-center justify-center gap-2 text-xl font-medium text-gray-800 leading-tight">
            {/* Icono de sector con shimmer a la izquierda del welcome */}
            <span className="relative inline-block mr-2">
              {userData?.sector === 'health' ? (
                <FaRegHospital className="text-2xl text-red-500 drop-shadow-lg" title="Health sector" />
              ) : userData?.sector === 'construction' ? (
                <FaHardHat className="text-2xl text-yellow-500 drop-shadow-lg" title="Construction sector" />
              ) : null}
              <span className="absolute inset-0 rounded-full shimmer-mask pointer-events-none" />
            </span>
            {t('workerDashboard.welcome', { name: userData?.firstName })}
            <span
              title={locationCapturing
                ? t('workerDashboard.capturingLocation') || 'Capturando ubicación del worker'
                : (locationSaved
                  ? t('workerDashboard.locationCaptured') || 'Ubicación del worker capturada'
                  : t('workerDashboard.locationNotCaptured') || 'No se pudo capturar la ubicación del worker')
              }
            >
              <FaMapMarkerAlt className={locationCapturing ? 'text-green-500 text-lg animate-pulse' : (locationSaved ? 'text-green-500 text-lg' : 'text-gray-400 text-lg')} />
            </span>
          </div>
          <div className="text-4xl font-bold text-red-600 leading-tight">
            {formatTime(new Date())}
          </div>
          <div className="text-xl font-medium text-gray-800 leading-tight">
            {formatDate(new Date())}
          </div>
        </div>
        <span className="px-6 py-3 rounded-full bg-gradient-to-br from-red-600 to-pink-500 text-white font-semibold shadow-lg transform -translate-y-0.5 transition-transform duration-300 justify-self-end">
          {t('workerDashboard.status')}: {userData?.status === 'active' ? t('workerDashboard.active') : t('workerDashboard.pending')}
        </span>
      </header>

      {/* Resumen superior */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10 w-full">
        {/* Proyectos asignados */}
        <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
          <span className="text-3xl font-bold text-red-600 mb-1">{activeProjects.length}</span>
          <span className="text-gray-700 font-medium text-sm text-center">{t('workerDashboard.summaryAssignedProjects', 'Assigned Projects')}</span>
        </div>
        {/* Tareas asignadas */}
        <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
          <span className="text-3xl font-bold text-red-600 mb-1">{assignedTasks.length}</span>
          <span className="text-gray-700 font-medium text-sm text-center">{t('workerDashboard.summaryAssignedTasks', 'Assigned Tasks')}</span>
        </div>
        {/* Ganancias totales */}
        <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
          <span className="text-3xl font-bold text-green-600 mb-1">${assignedTasks.reduce((acc, t) => acc + (t.finalTotal || 0), 0).toLocaleString()}</span>
          <span className="text-gray-700 font-medium text-sm text-center">{t('workerDashboard.summaryTotalEarnings', 'Total Earnings')}</span>
          <div className="mt-2 w-full text-xs text-gray-600 text-center">
            <div>{t('workerDashboard.summaryEarningsPending', 'Pending:')} <span className="font-semibold text-yellow-700">${assignedTasks.filter(t => t.columnId !== 'done').reduce((acc, t) => acc + (t.finalTotal || 0), 0).toLocaleString()}</span></div>
            <div>{t('workerDashboard.summaryEarningsReceived', 'Received:')} <span className="font-semibold text-green-700">${assignedTasks.filter(t => t.columnId === 'done').reduce((acc, t) => acc + (t.finalTotal || 0), 0).toLocaleString()}</span></div>
          </div>
        </div>
        {/* Progreso general */}
        <div className="bg-white/90 rounded-2xl shadow-xl p-6 flex flex-col items-center justify-center border border-white/30">
          <div className="relative w-14 h-14 mb-1">
            <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
              <path
                className="text-gray-200"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-blue-600"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${assignedTasks.length > 0 ? Math.round(assignedTasks.filter(t => t.columnId === 'done').length / assignedTasks.length * 100) : 0}, 100`}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-blue-600 text-lg font-semibold">{assignedTasks.length > 0 ? Math.round(assignedTasks.filter(t => t.columnId === 'done').length / assignedTasks.length * 100) : 0}%</span>
            </div>
          </div>
          <span className="text-gray-700 font-medium text-sm text-center">{t('workerDashboard.summaryGeneralProgress', 'General Progress')}</span>
        </div>
      </div>

      {/* Grid superior de 3 columnas */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8 w-full relative">
        {/* Mi perfil: menos ancho */}
        <section className="bg-white rounded-[20px_40px_20px_40px] p-6 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10 md:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">{t('workerDashboard.myProfile')}</h2>
          </div>
          <div className="flex flex-col items-center justify-center gap-4">
            {currentUser?.uid && (
              <QRCodeSVG value={currentUser.uid} size={180} level="H" includeMargin={true} />
            )}
            <div className="flex flex-col gap-3 w-full">
              <button
                className="px-6 py-3 bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] hover:from-[#D50B3F] hover:to-[#B3122C] text-white rounded-xl font-bold text-base shadow-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#D50B3F]/30 backdrop-blur-md bg-white/30 border border-white/20"
                onClick={() => navigate(`/profile`)}
              >
                {t('workerDashboard.goToProfile')}
              </button>
            </div>
          </div>
        </section>
        {/* Certificaciones: igual ancho */}
        <div className="md:col-span-4">
          <CertificationsCard />
        </div>
        {/* Solicitudes nuevas: más ancho */}
        <section className="bg-white rounded-[20px_40px_20px_40px] p-8 shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:shadow-xl hover:z-10 md:col-span-5">
          <AssignmentRequests />
        </section>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 w-full relative">
        <ActiveProjects />
        <AssignedTasks />
        <AssignedBusinesses />
      </div>

      {/* Modal de Notificaciones */}
      {notificationsModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">{t('workerDashboard.notificationsTitle')}</h2>
              <button
                onClick={() => setNotificationsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <FaTimes />
              </button>
            </div>

            {/* Pestañas */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                  activeTab === 'unread'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('workerDashboard.unreadTab')} ({notifications.filter(n => !n.read).length})
              </button>
              <button
                onClick={() => setActiveTab('read')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                  activeTab === 'read'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('workerDashboard.readTab')} ({notifications.filter(n => n.read).length})
              </button>
            </div>

            {/* Contenido de las pestañas */}
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {activeTab === 'unread' ? (
                <UnreadNotifications 
                  notifications={notifications.filter(n => !n.read)}
                  onMarkAsRead={handleMarkAsRead}
                />
              ) : (
                <ReadNotifications 
                  notifications={notifications.filter(n => n.read)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerDashboard; 